import { zodResolver } from '@hookform/resolvers/zod'
import { authPillButtonClass, Button, Form, FormTextField, Icons } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { AuthLayout } from '../components/auth/AuthLayout'
import { api, getTRPCErrorMessage } from '../core/trpc'

const PATH = '/forgot-password'

const forgotPasswordSchema = z.object({
    email: z.string().email(),
})

type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>

const ForgotPasswordPage = () => {
    const { t } = useTranslation()

    const [sent, setSent] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const form = useForm<ForgotPasswordSchema>({
        resolver: zodResolver(forgotPasswordSchema),
        defaultValues: { email: '' },
    })

    const { mutateAsync: sendResetLink, isPending } = api.user.sendPasswordResetLink.useMutation()

    const onSubmit = async (data: ForgotPasswordSchema) => {
        setError(null)

        try {
            await sendResetLink(data)
            setSent(true)
        } catch (caught) {
            setError(getTRPCErrorMessage(caught))
        }
    }

    return (
        <AuthLayout
            title={t('frontend.auth.forgotPasswordTitle')}
            footer={
                <Link className="text-primary underline" to="/login">
                    {t('frontend.auth.backToLogin')}
                </Link>
            }
        >
            {sent ? (
                <div className="flex flex-col items-center gap-4">
                    <Check className="size-12 text-primary" />
                    <p className="text-center">{t('frontend.auth.resetLinkSent')}</p>
                </div>
            ) : (
                <>
                    <Form {...form}>
                        <FormTextField
                            control={form.control}
                            name="email"
                            label={t('frontend.auth.email')}
                            inputProps={{
                                type: 'email',
                                autoComplete: 'email',
                                disabled: isPending,
                            }}
                        />
                    </Form>

                    {error && <p className="text-sm text-destructive">{error}</p>}

                    <Button
                        className={authPillButtonClass}
                        disabled={isPending}
                        onClick={form.handleSubmit(onSubmit)}
                    >
                        {isPending && <Icons.Spinner className="mr-2 h-4 animate-spin" />}
                        {t('frontend.auth.sendResetLink')}
                    </Button>
                </>
            )}
        </AuthLayout>
    )
}

export const Route = createFileRoute(PATH)({
    ssr: false,
    head: () => ({ meta: [{ title: 'Long-Y - Elfelejtett jelszó' }] }),
    component: ForgotPasswordPage,
})
